import { eventType, noonReportType } from './enums.js';
import { mergeOverlappingEventSequences } from './aol-overlap-utils.js';
import { dateToTimeMs, getEventLocalTimeMs, getEventUtcMs } from './event-time-keys.js';
import * as dateTimeFormatter from 'odfjell-date-time-utils/dateTimeFormatter.js';
import * as dateTimeUtils from 'odfjell-date-time-utils/dateTimeUtils.js';

const MIN_TIME_MS = -8640000000000000; // Minimum value of date
const MAX_TIME_MS = 8640000000000000; // Maximum value of date
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Sort events chronologically: true UTC instant (utcMs), then port local time (localTimeMs), then creation time.
 * UTC first keeps International Date Line crossings monotonic (local times jump by a day there). Neither key depends on the
 * browser zone, so a local time in the browser's DST gap or overlap cannot change the order.
 */
export const compareEventsByTime = (a, b) => {
    const aUtc = getEventUtcMs(a);
    const bUtc = getEventUtcMs(b);

    if (aUtc !== null && bUtc !== null) {
        const utcDiff = aUtc - bUtc;
        if (utcDiff !== 0) return utcDiff;
    }

    const aLocal = getEventLocalTimeMs(a);
    const bLocal = getEventLocalTimeMs(b);
    if (aLocal !== null && bLocal !== null) {
        const localDiff = aLocal - bLocal;
        if (localDiff !== 0) return localDiff;
    }

    // Tie-breaker: event creation time (consistent with localAolGenerator.ts)
    const aCreated = a.created ? new Date(a.created).getTime() : 0;
    const bCreated = b.created ? new Date(b.created).getTime() : 0;
    return aCreated - bCreated;
};

// AOL item boundary: the browser-local Date (for display and the activity helpers) plus the DST-free port local time (ms, read as UTC fields) used for day assignment.
const eventLocalPoint = (ev) => {
    const date = new Date(ev.eventDate.actualDateTimeLocal);
    return { date, timeMs: getEventLocalTimeMs(ev) ?? dateToTimeMs(date) };
};

const eventUtcDate = (ev) => {
    const utcMs = ev.eventDate.actualDateTimeUtc ? getEventUtcMs(ev) : null;
    return utcMs === null ? null : new Date(utcMs);
};

const maxLocalPoint = () => ({ date: new Date(MAX_TIME_MS), timeMs: MAX_TIME_MS });
const firstEventStopPoint = (evList) =>
    evList.length > 0 && evList[0].eventDate.actualDateTimeLocal ? eventLocalPoint(evList[0]) : maxLocalPoint();

export const getAolItemsForDates = (evList, portCalls, fromAolDate, toAolDate, isSimpleMode = false) => {
    evList = evList.sort(compareEventsByTime);
    const aolItemsPerDate = {};
    const berthCalls = getBerthCalls(portCalls);
    const sortedPortCallIds = portCalls.map((pc) => pc.portCallId ?? pc.imosPortCallId).filter(Boolean);
    const aolItemsPortCall = getAolItemsForPortCall(evList, berthCalls, isSimpleMode, sortedPortCallIds);

    if (aolItemsPortCall.length > 0) {
        let aolDate = fromAolDate;
        while (aolDate <= toAolDate) {
            const aolItems = [];
            const dateKey = dateTimeFormatter.formatDateTime(aolDate, 'yyyy-MM-ddTHH:mm');
            for (const aolItem of aolItemsPortCall) {
                const included = includeAolItem(aolDate, aolItem);
                if (included) {
                    const start = getAolStartTime(aolDate, aolItem);
                    const stop = getAolStopTime(aolDate, aolItem);
                    const newAolItem = {
                        startTimeLocalTime: start.date,
                        stopTimeLocalTime: stop.date,
                        startTimeMs: start.timeMs,
                        stopTimeMs: stop.timeMs,
                        startTimeUtc: aolItem.startTimeUtc || null,
                        stopTimeUtc: aolItem.stopTimeUtc || null,
                        noonReportType: aolItem.noonReportType,
                        imosPortCallId: aolItem.imosPortCallId,
                        isValid: aolItem.isValid,
                        errorEvents: aolItem.errorEvents,
                    };
                    aolItems.push(newAolItem);
                }
            }
            aolItemsPerDate[dateKey] = aolItems;
            aolDate = dateTimeUtils.addDateDays(aolDate, 1);
        }
    }

    const sortedAolItemsPerDate = Object.keys(aolItemsPerDate)
        .sort() // Sort the keys
        .reduce((acc, key) => {
            acc[key] = aolItemsPerDate[key]; // Build a new object
            return acc;
        }, {});
    return sortedAolItemsPerDate;
};

export const getAolItemsForDate = (evList, portCalls, aolDate, isSimpleMode = false) => {
    evList = evList.sort(compareEventsByTime);
    const aolItems = [];
    const berthCalls = getBerthCalls(portCalls);
    const sortedPortCallIds = portCalls.map((pc) => pc.portCallId ?? pc.imosPortCallId).filter(Boolean);

    var aolItemsPortCall = getAolItemsForPortCall(evList, berthCalls, isSimpleMode, sortedPortCallIds);
    if (aolItemsPortCall.length > 0) {
        for (const aolItem of aolItemsPortCall) {
            if (includeAolItem(aolDate, aolItem)) {
                const start = getAolStartTime(aolDate, aolItem);
                aolItem.startTimeLocalTime = start.date;
                aolItem.startTimeMs = start.timeMs;
                const stop = getAolStopTime(aolDate, aolItem);
                aolItem.stopTimeLocalTime = stop.date;
                aolItem.stopTimeMs = stop.timeMs;
                aolItems.push(aolItem);
            }
        }
    }
    return aolItems;
};

const getPreviousPortCallId = (currentPortCallId, rotationPortCallIds) => {
    if (!rotationPortCallIds || rotationPortCallIds.length === 0) return currentPortCallId;
    const idx = rotationPortCallIds.indexOf(currentPortCallId);
    if (idx > 0) return rotationPortCallIds[idx - 1];
    return currentPortCallId;
};

export const getAolItemsForPortCall = (evList, berthCalls, isSimpleMode = false, rotationPortCallIds = null) => {
    const aolItems = [];
    // Overlapping barge sequences (e.g. 184,184,185,185): only first open / last close drive AOL boundaries
    evList = mergeOverlappingEventSequences(evList);
    // Filter evList
    // evList = isSimpleMode ? filterSimpleAolTypeEvents(evList) : getEventsFilteredOnEospSops(evList);

    // Add valid sea item
    const seaFirstStop = firstEventStopPoint(evList);
    const aolItemSeaFirst = {
        startTimeLocalTime: new Date(MIN_TIME_MS),
        stopTimeLocalTime: seaFirstStop.date,
        startTimeMs: MIN_TIME_MS,
        stopTimeMs: seaFirstStop.timeMs,
        startTimeUtc: null,
        stopTimeUtc: evList.length > 0 ? eventUtcDate(evList[0]) : null,
        noonReportType: noonReportType.sea,
        imosPortCallId: evList.length > 0 ? getPreviousPortCallId(evList[0].portCallId, rotationPortCallIds) : null,
        isValid: true,
        errorEvents: [],
    };

    aolItems.push(aolItemSeaFirst);

    if (evList.length > 0) {
        // If first <> EOSP add invalid port item and return
        if (evList[0].eventTypeId != eventType.eosp) {
            const invalidStop = firstEventStopPoint(evList);
            const aolItem = {
                startTimeLocalTime: new Date(MIN_TIME_MS),
                stopTimeLocalTime: invalidStop.date,
                startTimeMs: MIN_TIME_MS,
                stopTimeMs: invalidStop.timeMs,
                startTimeUtc: null,
                stopTimeUtc: eventUtcDate(evList[0]),
                noonReportType: getAolType(
                    evList[0],
                    hasFutureBerthCallsWithNoEvents(berthCalls, evList, evList[0].portClusterCallId),
                    evList.length > 2 ? evList[1] : null,
                    isSimpleMode
                ),
                imosPortCallId: evList[0].portCallId,
                isValid: false,
                errorEvents: createErrorEventList(evList[0]),
            };

            aolItems.push(aolItem);
        } else {
            const firstStart = eventLocalPoint(evList[0]);
            const aolItemFirst = {
                startTimeLocalTime: firstStart.date,
                stopTimeLocalTime: new Date(MAX_TIME_MS),
                startTimeMs: firstStart.timeMs,
                stopTimeMs: MAX_TIME_MS,
                startTimeUtc: eventUtcDate(evList[0]),
                stopTimeUtc: null,
                noonReportType: getAolType(
                    evList[0],
                    hasFutureBerthCallsWithNoEvents(berthCalls, evList, evList[0].portClusterCallId),
                    evList.length > 2 ? evList[1] : null,
                    isSimpleMode
                ),
                imosPortCallId: evList[0].portCallId,
                isValid: true,
                errorEvents: [],
            };

            aolItems.push(aolItemFirst);
            let keepGoing = evList.length > 1;
            let ind = 1;
            while (keepGoing) {
                // If next event is valid: add valid aol item
                const _isEventValid = isSimpleMode ? isSimpleEventValid(evList[ind - 1], evList[ind]) : isEventValid(evList[ind - 1], evList[ind]);

                if (_isEventValid) {
                    const aolType = getAolType(
                        evList[ind],
                        hasFutureBerthCallsWithNoEvents(berthCalls, evList, evList[ind].portClusterCallId),
                        evList.length > ind + 1 ? evList[ind + 1] : null,
                        isSimpleMode
                    );
                    const previousAolItem = aolItems[aolItems.length - 1];
                    const start = eventLocalPoint(evList[ind]);
                    const startTimeUtc = eventUtcDate(evList[ind]);
                    if (previousAolItem.noonReportType != aolType) {
                        const aolItem = {
                            startTimeLocalTime: start.date,
                            stopTimeLocalTime: new Date(MAX_TIME_MS),
                            startTimeMs: start.timeMs,
                            stopTimeMs: MAX_TIME_MS,
                            startTimeUtc: startTimeUtc,
                            stopTimeUtc: null,
                            noonReportType: aolType,
                            imosPortCallId: evList[ind].portCallId,
                            isValid: true,
                            errorEvents: [],
                        };
                        aolItems.push(aolItem);
                        previousAolItem.stopTimeLocalTime = start.date;
                        previousAolItem.stopTimeMs = start.timeMs;
                        previousAolItem.stopTimeUtc = startTimeUtc;
                    }
                    ind++;
                    keepGoing = ind < evList.length;
                } else {
                    const start = eventLocalPoint(evList[ind]);
                    const startTimeUtc = eventUtcDate(evList[ind]);
                    // If next event is not valid: add invalid aol item and return
                    const previousAolItem = aolItems[aolItems.length - 1];
                    const aolItem = {
                        startTimeLocalTime: start.date,
                        stopTimeLocalTime: new Date(MAX_TIME_MS),
                        startTimeMs: start.timeMs,
                        stopTimeMs: MAX_TIME_MS,
                        startTimeUtc: startTimeUtc,
                        stopTimeUtc: null,
                        noonReportType: getAolType(
                            evList[ind],
                            hasFutureBerthCallsWithNoEvents(berthCalls, evList, evList[ind].portClusterCallId),
                            evList.length > ind + 1 ? evList[ind + 1] : null,
                            isSimpleMode
                        ),
                        imosPortCallId: evList[ind].portCallId,
                        isValid: false,
                        errorEvents: createErrorEventList(evList[ind - 1], evList[ind]),
                    };
                    aolItems.push(aolItem);
                    previousAolItem.stopTimeLocalTime = start.date;
                    previousAolItem.stopTimeMs = start.timeMs;
                    previousAolItem.stopTimeUtc = startTimeUtc;
                    keepGoing = false;
                }
            }
        }
    }
    return aolItems;
};
const getBerthCalls = (portCalls) => {
    return portCalls
        .flatMap((x) => x.portCallItems.map((y) => [x.portClusterCallId ?? x.portCallId, y.berthCall]))
        .filter((x) => x[0] !== null)
        .map((x) => [x[0], x[1]]);
};

const getAolType = (ev, hasEmptyBerthCalls, nextEvent, isSimpleMode = false) => {
    if (isSimpleMode) {
        return getSimpleAolType(ev, nextEvent);
    } else {
        switch (ev.eventTypeId) {
            case eventType.eosp:
                return isArrival(ev.eventTypeId, hasEmptyBerthCalls, nextEvent) ? noonReportType.arrival : noonReportType.port;
            case eventType.sosp:
                return noonReportType.sea;
            case eventType.allFast:
                return noonReportType.port;
            case eventType.allClear:
                return isArrival(ev.eventTypeId, hasEmptyBerthCalls, nextEvent) ? noonReportType.port : noonReportType.departure;
            case eventType.wellAnchoredCargoOps:
                return noonReportType.port;
            case eventType.anchorAweighCargoOps:
                return isArrival(ev.eventTypeId, hasEmptyBerthCalls, nextEvent) ? noonReportType.port : noonReportType.departure;
            case eventType.wellAnchoredNoCargo:
                return noonReportType.port;
            case eventType.anchorAweighNoCargo:
                return isArrival(ev.eventTypeId, hasEmptyBerthCalls, nextEvent) ? noonReportType.port : noonReportType.departure;
            case eventType.dryDockingCommenced:
                return noonReportType.port;
            case eventType.dryDockingCompleted:
                return isArrival(ev.eventTypeId, hasEmptyBerthCalls, nextEvent) ? noonReportType.port : noonReportType.departure;
            case eventType.dryDockingBerthCommenced:
                return noonReportType.port;
            case eventType.dryDockingBerthCompleted:
                return isArrival(ev.eventTypeId, hasEmptyBerthCalls, nextEvent) ? noonReportType.port : noonReportType.departure;
            case eventType.bunkerBargeAlongsideAtBerth:
                return noonReportType.port;
            case eventType.bunkerBargeCastedOffAtBerth:
                return isArrival(ev.eventTypeId, hasEmptyBerthCalls, nextEvent) ? noonReportType.port : noonReportType.departure;
        }
        return noonReportType.sea;
    }
};

const isArrival = (eventTypeId, hasEmptyBerthCalls, nextEvent) => {
    if (nextEvent == null) return hasEmptyBerthCalls;
    switch (eventTypeId) {
        case eventType.eosp:
            return hasEmptyBerthCalls || isArrivalStartEvent(nextEvent.eventTypeId);
        case eventType.sosp:
            return hasEmptyBerthCalls;
        case eventType.allFast:
            return hasEmptyBerthCalls;
        case eventType.allClear:
            return hasEmptyBerthCalls || isArrivalStartEvent(nextEvent.eventTypeId);
        case eventType.wellAnchoredCargoOps:
            return hasEmptyBerthCalls;
        case eventType.anchorAweighCargoOps:
            return hasEmptyBerthCalls || isArrivalStartEvent(nextEvent.eventTypeId);
        case eventType.wellAnchoredNoCargo:
            return hasEmptyBerthCalls;
        case eventType.anchorAweighNoCargo:
            return hasEmptyBerthCalls || isArrivalStartEvent(nextEvent.eventTypeId);
        case eventType.dryDockingCommenced:
            return hasEmptyBerthCalls;
        case eventType.dryDockingCompleted:
            return hasEmptyBerthCalls || isArrivalStartEvent(nextEvent.eventTypeId);
        case eventType.dryDockingBerthCommenced:
            return hasEmptyBerthCalls;
        case eventType.dryDockingBerthCompleted:
            return hasEmptyBerthCalls || isArrivalStartEvent(nextEvent.eventTypeId);
        case eventType.bunkerBargeAlongsideAtBerth:
            return hasEmptyBerthCalls;
        case eventType.bunkerBargeCastedOffAtBerth:
            return hasEmptyBerthCalls || isArrivalStartEvent(nextEvent.eventTypeId);
    }
    return false;
};

const getSimpleAolType = (ev, nextEvent) => {
    switch (ev.eventTypeId) {
        case eventType.eosp:
            return isSimpleArrival(ev.eventTypeId, nextEvent) ? noonReportType.arrival : noonReportType.port;
        case eventType.sosp:
            return noonReportType.sea;
        case eventType.simpleBerthArrival:
            return noonReportType.port;
        case eventType.simpleBerthDeparture:
            return isSimpleArrival(ev.eventTypeId, nextEvent) ? noonReportType.port : noonReportType.departure;
        case eventType.wellAnchored:
            return noonReportType.port; // At anchor is a port activity
        case eventType.anchorAweigh:
            return isSimpleArrival(ev.eventTypeId, nextEvent) ? noonReportType.port : noonReportType.departure;
    }
    return noonReportType.sea;
};

const isSimpleArrival = (eventTypeId, nextEvent) => {
    if (nextEvent == null) return false;
    switch (eventTypeId) {
        case eventType.eosp:
            return isArrivalSimpleStartEvent(nextEvent.eventTypeId);
        case eventType.sosp:
            return false;
        case eventType.simpleBerthArrival:
            return true;
        case eventType.simpleBerthDeparture:
            return isArrivalSimpleStartEvent(nextEvent.eventTypeId);
        case eventType.wellAnchored:
            return true; // At anchor, waiting for next event
        case eventType.anchorAweigh:
            return isArrivalSimpleStartEvent(nextEvent.eventTypeId);
    }
    return false;
};

const isArrivalSimpleStartEvent = (eventTypeId) => {
    return eventTypeId == eventType.simpleBerthArrival;
};

const isArrivalStartEvent = (eventTypeId) => {
    return (
        eventTypeId === eventType.allFast ||
        eventTypeId === eventType.wellAnchoredCargoOps ||
        eventTypeId === eventType.wellAnchoredNoCargo ||
        eventTypeId === eventType.dryDockingCommenced ||
        eventTypeId === eventType.dryDockingBerthCommenced ||
        eventTypeId === eventType.bunkerBargeAlongsideAtBerth
    );
};

const hasFutureBerthCallsWithNoEvents = (bcList, evList, portClusterCallId) => {
    if (portClusterCallId !== null && bcList.length > 0) {
        const bcListInCluster = bcList.filter((o) => o[0] === portClusterCallId);
        if (bcListInCluster.length > 0) {
            const emptyBerthCalls = bcListInCluster.filter(
                (bc) => !evList.some((ev) => ev.berthCallId !== null && ev.berthCallId === bc[1].berthCallId)
            );
            return emptyBerthCalls.length > 0;
        }
    }
    return false;
};

const createErrorEventList = (...events) => events.map((ev) => createErrorEvent(ev));

const createErrorEvent = (ev) => ({
    eventId: ev.eventId,
    eventDate: ev.eventDate,
    eventTypeId: ev.eventTypeId,
    portCallId: ev.portCallId,
    portClusterCallId: ev.portClusterCallId,
    portClusterId: ev.portClusterId,
    berthId: ev.berthId,
    voyageNumber: ev.voyageNumber,
    portName: '',
    berthName: '',
});

const isSimpleEventValid = (prevEv, ev) => {
    if (prevEv.portClusterCallId !== ev.portClusterCallId && prevEv.eventTypeId !== eventType.sosp) {
        return false;
    }

    switch (prevEv.eventTypeId) {
        case eventType.eosp:
            return (
                ev.eventTypeId === eventType.sosp ||
                ev.eventTypeId === eventType.simpleBerthArrival ||
                ev.eventTypeId === eventType.simpleBerthDeparture ||
                ev.eventTypeId === eventType.wellAnchored // Support anchoring after EOSP in simple mode
            );
        case eventType.sosp:
            return prevEv.portClusterCallId !== ev.portClusterCallId;
        case eventType.simpleBerthArrival:
            return ev.eventTypeId === eventType.simpleBerthDeparture || ev.eventTypeId === eventType.wellAnchored; // Vessel may shift from berth to anchorage
        case eventType.simpleBerthDeparture:
            return ev.eventTypeId === eventType.sosp || ev.eventTypeId === eventType.wellAnchored;
        case eventType.wellAnchored:
            return ev.eventTypeId === eventType.anchorAweigh;
        case eventType.anchorAweigh:
            return (
                ev.eventTypeId === eventType.sosp ||
                ev.eventTypeId === eventType.simpleBerthArrival ||
                ev.eventTypeId === eventType.simpleBerthDeparture ||
                ev.eventTypeId === eventType.wellAnchored // Support multiple anchoring sequences
            );
        default:
            return false;
    }
};

const isEventValid = (prevEv, ev) => {
    if (
        prevEv.portClusterCallId &&
        ev.portClusterCallId &&
        prevEv.portClusterCallId !== ev.portClusterCallId &&
        prevEv.eventTypeId !== eventType.sosp
    ) {
        return false;
    }

    switch (prevEv.eventTypeId) {
        case eventType.eosp:
        case eventType.allClear:
        case eventType.anchorAweighCargoOps:
        case eventType.anchorAweighNoCargo:
        case eventType.dryDockingCompleted:
        case eventType.dryDockingBerthCompleted:
        case eventType.bunkerBargeCastedOffAtBerth:
            return ev.eventTypeId === eventType.sosp || isArrivalStartEvent(ev.eventTypeId);
        case eventType.sosp:
            if (!prevEv.portClusterCallId && !ev.portClusterCallId) return true;
            return prevEv.portClusterCallId !== ev.portClusterCallId;
        case eventType.allFast:
            return ev.eventTypeId === eventType.allClear;
        case eventType.wellAnchoredCargoOps:
            return ev.eventTypeId === eventType.anchorAweighCargoOps;
        case eventType.wellAnchoredNoCargo:
            return ev.eventTypeId === eventType.anchorAweighNoCargo;
        case eventType.dryDockingCommenced:
            return ev.eventTypeId === eventType.dryDockingCompleted;
        case eventType.dryDockingBerthCommenced:
            return ev.eventTypeId === eventType.dryDockingBerthCompleted;
        case eventType.bunkerBargeAlongsideAtBerth:
            return ev.eventTypeId === eventType.bunkerBargeCastedOffAtBerth;
        default:
            return false;
    }
};

// Day windows are noon-to-noon on the vessel's port local time. The calendar day comes from aolDate; the boundaries are
// Date.UTC(y, m, d, 12) so they never depend on the browser zone or its DST transitions.
const getNoonTimeMs = (aolDate) => {
    const day = dateTimeUtils.getLuxonDateTime(aolDate);
    return Date.UTC(day.year, day.month - 1, day.day, 12);
};

// Browser-local Date with noon in its fields, for display and the Date based activity helpers.
const getNoonDate = (aolDate, daysBack) =>
    dateTimeUtils.getLuxonDateTime(aolDate).set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).minus({ days: daysBack }).toJSDate();

const includeAolItem = (aolDate, aolItem) => {
    const aolStopTimeMs = getNoonTimeMs(aolDate);
    const aolStartTimeMs = aolStopTimeMs - DAY_MS;
    const { startTimeMs, stopTimeMs } = aolItem;

    // IDL-spanning item: local start > local stop means the item crosses the date line.
    // Include it in the day containing the stop time (where the vessel arrives).
    if (startTimeMs > stopTimeMs) {
        const stopInWindow = aolStartTimeMs <= stopTimeMs && stopTimeMs <= aolStopTimeMs;
        const startInWindow = aolStartTimeMs <= startTimeMs && startTimeMs <= aolStopTimeMs;
        return stopInWindow || startInWindow;
    }

    const startBetween = aolStartTimeMs >= startTimeMs && aolStartTimeMs < stopTimeMs;
    const stopBetween = aolStopTimeMs > startTimeMs && aolStopTimeMs <= stopTimeMs;
    const spanning = aolStartTimeMs < startTimeMs && aolStopTimeMs > stopTimeMs;

    return startBetween || stopBetween || spanning;
};

const getAolStartTime = (aolDate, aolItem) => {
    // For IDL-spanning items (start > stop in local time), don't clip start
    const own = { date: aolItem.startTimeLocalTime, timeMs: aolItem.startTimeMs };
    if (aolItem.startTimeMs > aolItem.stopTimeMs) return own;
    const boundaryTimeMs = getNoonTimeMs(aolDate) - DAY_MS;
    return boundaryTimeMs > aolItem.startTimeMs ? { date: getNoonDate(aolDate, 1), timeMs: boundaryTimeMs } : own;
};

const getAolStopTime = (aolDate, aolItem) => {
    // For IDL-spanning items (start > stop in local time), don't clip stop
    const own = { date: aolItem.stopTimeLocalTime, timeMs: aolItem.stopTimeMs };
    if (aolItem.startTimeMs > aolItem.stopTimeMs) return own;
    const boundaryTimeMs = getNoonTimeMs(aolDate);
    return boundaryTimeMs < aolItem.stopTimeMs ? { date: getNoonDate(aolDate, 0), timeMs: boundaryTimeMs } : own;
};
